<script setup>
  import {
    computed,
    ref,
    onBeforeUnmount,
    defineAsyncComponent,
  } from 'vue';

  import mobile from '@js/misc/mobile';
  import contrast from '@js/misc/contrast';

  import CredereLoading from '@/components/Credere/Loading.vue';
  import CredereButton from '@/components/Credere/Button.vue';
  import CredereModal from '@/components/Credere/Modal.vue';
  import CredereTip from '@/components/Credere/Tip.vue';

  import filled from './javascripts/filled';
  import { VISIBLE_OPTIONS_TRUE, ordered } from './javascripts/options';
  import { destroyer } from './javascripts/identified';

  import useSelector from './fields/selector';
  import { CUSTOM_VALUE, CREDERE_VALUE, CUSTOM_LABEL } from './fields/color';

  const QrcodeVue = defineAsyncComponent(() => import('qrcode.vue'));

  const props = defineProps({
    values: { type: Object, default: () => ({}), required: false },
    domains: { type: Object, default: () => ({}), required: false },

    name: { type: String, required: true },
    leaf: { type: Object, required: true },

    properties: { type: Object, required: true },
    variant: { type: String, required: false },

    value: { type: String, default: '', required: false },

    error: { type: String, default: '', required: false },
    loading: { type: Boolean, default: false, required: false },
    visibility: { type: String, default: VISIBLE_OPTIONS_TRUE, required: false },
    readonly: { type: Boolean, default: false, required: false },
    tip: { type: String, default: '', required: false },

    destroyed: { type: Boolean, default: false, required: false },

    inline: { type: Boolean, default: false, required: false },
    qrcodable: { type: Boolean, default: false, required: false },
  });

  const ALL = '_TODOS';

  const emit = defineEmits(['update:value', 'update:values', 'blur', 'destroy']);

  const refSelectSearch = ref();
  const refSelectList = ref();

  const uploadable = ref(false);
  const qrcoding = ref(false);

  let fileName;
  let fileLink;
  let fileStyle;
  let filePreview;

  let fileToggle;
  let fileVisible;

  if (props.properties.type === 'file') {
    import('./fields/uploader').then((module) => {
      ({
        name: fileName,
        link: fileLink,
        style: fileStyle,
        preview: filePreview,

        toggle: fileToggle,
        visible: fileVisible,
      } = module.default(props, uploaded));

      uploadable.value = true;
      qrcoding.value = props.qrcodable && !mobile;
    });
  }

  function color(hexa) {
    return hexa === CUSTOM_VALUE || /^#([a-f0-9]{6}|[a-f0-9]{3})$/.test(hexa?.toLowerCase?.());
  }

  const destroyable = destroyer(props.name);

  // @TODO: desabilitar campos
  const _disabled = computed(() => {
    if (props.destroyed) {
      return true;
    }

    return props.readonly || props.loading;
  });

  const value = computed({
    get() {
      return props.value;
    },
    set(v) {
      emit('update:value', v);
    },
  });

  const {
    visible: selectVisible,
    query: selectQuery,
    limited: selectLimited,
    results: selectResults,
    toggle: selectToggle,
    search: selectSearch,
    prev: selectPrevious,
    next: selectNext,
    first: selectFirst,
  } = useSelector(props, value, refSelectSearch, refSelectList, _disabled);

  const label = computed(() => {
    const _label = props
      .properties
      .options
      ?.find((i) => i.value === value.value)?.label;

    if (color(value.value) && !_label) {
      return value.value ? value.value : CUSTOM_LABEL;
    }

    return _label;
  });

  const all = computed(() => props.properties.options.map((i) => i.value).join(','));
  const full = computed(() => ordered(props.value) === ordered(all.value));
  const multiple = computed(() => (value.value || '').split(',').map((i) => String(i)));
  const optionized = computed(() => props.properties.optionized
    && ['boolean', 'select', 'checkbox', 'radio'].includes(props.properties.type));

  function optionizedIcon(option) {
    return [
      'fa-solid',
      props.properties.type === 'checkbox'
        ? `fa-square${(multiple.value.includes(option.value) || full.value) ? '-check' : ''}`
        : `fa-circle${value.value === option.value ? '-dot' : ''}`,
    ];
  }

  function toggle(bool) {
    if (props.properties.type === 'select') {
      selectToggle(bool);
    } else if (props.properties.type === 'file') {
      fileToggle?.(bool);
    }
  }

  function force(option) {
    if (_disabled.value || option.disabled) {
      return;
    }

    let _value = option.value;

    if (props.properties.type === 'checkbox') {
      if (option.value === ALL) {
        _value = all.value;
      } else {
        _value = [...multiple.value];
        const index = _value.findIndex((i) => i === option.value);

        if (index > -1) {
          _value.splice(index, 1);
        } else {
          _value.push(option.value);
        }

        _value = _value.filter((i) => i).join(',');
      }
    }

    input({ target: { value: _value } });
    blur();
  }

  function input(e) {
    if (_disabled.value) {
      return false;
    }

    value.value = props.properties.type === 'file' ? e.target.files : e.target.value;
    toggle(false);

    return true;
  }

  function blur() {
    if (_disabled.value) {
      return false;
    }

    emit('blur');

    return true;
  }

  function destroy() {
    emit('destroy');
  }

  let copyTimeout;
  const copied = ref(false);
  async function copy() {
    if (copied.value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value.value);

      copied.value = true;

      clearTimeout(copyTimeout);
      copyTimeout = setTimeout(() => { copied.value = false; }, 3000);
    } catch (err) { /* */ }
  }
  onBeforeUnmount(() => clearTimeout(copyTimeout));

  function uploaded(values) {
    toggle(false);

    emit('update:values', values);
  }

  const _picking = ref(CREDERE_VALUE);

  function picking(v) {
    const styles = {};

    if (v === CUSTOM_VALUE) {
      styles.backgroundColor = _picking.value || 'transparent';
      styles['--picking'] = `var(--token-color-constant-${contrast(styles.backgroundColor) ? 'dark' : 'light'})`;
    } else {
      styles.backgroundColor = v;
    }

    return styles;
  }

  const options = computed(() => {
    if (props.properties.type === 'checkbox') {
      return [{
        label: 'Todos',
        value: ALL,
      }].concat(props.properties.options);
    }

    return props.properties.options;
  });

  const _pickable = computed(() => props.properties.options.map((i) => i.value.toLowerCase()));
  function picked(e) {
    const _color = (e.target.value || '').toLowerCase();

    if (_pickable.value.includes(_color)) {
      _picking.value = null;
    }

    force({ value: _color.toUpperCase() });
  }

  const variant = computed(() => window.CREDERE.variants?.field || props.variant);

  const classes = computed(() => ({
    'credere-form-field': true,

    [`variant--${props.properties.type}`]: true,
    [`variant--field-1${variant.value?.toLowerCase()}`]: variant.value,
    'variant--required': props.properties.required,
    'variant--destroy': destroyable,

    'state--error': props.error && !props.destroyed,
    'state--loading': props.loading,
    'state--empty': !filled(value.value),
    'state--disabled': _disabled.value,
  }));
</script>

<template>
  <fieldset :class="classes">
    <label
      v-if="!inline"

      :for="name"

      class="credere-form-field-label"

      @click.prevent="focus"
    >
      <span class="credere-form-field-legend">
        <template v-if="(destroyable && !properties.hidden) || !leaf.label">
          &nbsp;
        </template>

        <template v-else>
          {{ leaf.label }}<span class="credere-form-field-required">*</span>
        </template>

        <credere-tip v-if="tip">
          {{ tip }}
        </credere-tip>
      </span>

      <font-awesome-icon
        v-if="value && properties.type === 'file'"
        icon="check"
      />

      <credere-loading v-if="loading" />
    </label>

    <template v-if="optionized">
      <template v-if="properties.type === 'boolean'">
        <credere-button
          v-if="destroyable"
          class="credere-form-field-destroy"
          :icon="destroyed ? 'rotate-left' : 'trash'"
          color="danger"
          @click.stop.prevent="destroy"
        />

        <ul class="credere-form-field-booleans">
          <li
            v-for="option in properties.options"
            :key="`${option.label}-${option.value}`"

            class="credere-form-field-boolean"
          >

            <label class="credere-form-field-sublabel">
              <input
                class="credere-form-field-subinput"
                type="radio"

                :name="name"

                :disabled="_disabled && value !== option.value"
                :checked="value === option.value"

                :value="option.value"
                @input="force(option)"
              />

              <span class="credere-form-field-input">
                {{ option.label }}
              </span>
            </label>
          </li>
        </ul>
      </template>

      <ul
        v-else-if="['radio', 'checkbox'].includes(properties.type)"
        class="credere-form-field-checkboxes"
      >
        <li
          v-for="option in options"
          :key="`${option.label}-${option.value}`"

          class="credere-form-field-checkbox"
        >
          <label class="credere-form-field-sublabel">
            <input
              class="credere-form-field-subinput"
              :type="properties.type"

              :name="name"

              :disabled="_disabled || (full && option.value === ALL)"
              :checked="multiple.includes(option.value) || (full && option.value === ALL)"

              :value="option.value"
              @input="force(option)"
            />

            <span class="credere-form-field-input">
              <font-awesome-icon
                class="credere-form-field-checkbox-icon"
                :icon="optionizedIcon(option)"
              />{{ option.label }}
            </span>
          </label>
        </li>
      </ul>

      <template v-else>
        <span
          v-if="selectVisible"

          class="credere-form-field-select-overlay"

          @click.self="selectToggle"
        />

        <div
          class="credere-form-field-input"
          :tabindex="_disabled ? -1 : 0"

          @click.self="selectToggle"
          @keydown.self.enter.prevent="selectToggle"
          @keydown.self.down.prevent="selectToggle"
          @keydown.self.space.prevent="selectToggle"
          @keydown.self="selectSearch"
        >
          <input
            v-if="selectVisible"
            ref="refSelectSearch"

            class="credere-form-field-select-search"

            type="text"
            placeholder="Pesquisar..."

            v-model="selectQuery"

            @keydown.tab.exact.prevent="selectFirst"
            @keydown.down.prevent="selectFirst"

            @keydown.enter.prevent
            @keydown.shift.tab="selectToggle"
            @keydown.esc="selectToggle"
          >

          <template v-else>
            <template v-if="filled(value)">
              {{ label || value }}
            </template>

            <template v-else>
              {{ leaf.description || 'Selecione' }}
            </template>

            <font-awesome-icon
              icon="chevron-down"
              @click.stop="selectToggle"
            />
          </template>

          <div
            v-show="selectVisible"

            class="credere-form-field-select"
          >
            <ul
              ref="refSelectList"
              class="credere-form-field-select-options"
              :style="`--limited: ${selectLimited}px`"
            >
              <template v-if="selectResults.length">
                <li
                  v-for="option in selectResults"
                  :key="`${option.label}-${option.value}`"

                  class="credere-form-field-select-option"
                >
                  <label
                    class="credere-form-field-sublabel"

                    @keydown.up.prevent="selectPrevious"
                    @keydown.down.prevent="selectNext"

                    @keydown.tab.exact="selectToggle"

                    @keydown.enter.prevent="force(option)"
                    @keydown.space.prevent="force(option)"

                    @click.prevent="force(option)"
                  >
                    <input
                      class="credere-form-field-subinput"
                      type="radio"

                      :name="name"
                      tabindex="-1"

                      :checked="value === option.value"
                      :disabled="option.disabled"
                      :value="option.value"
                      :value-label="option.label"
                    />

                    <span class="credere-form-field-select-text">
                      <template v-if="color(option.value)">
                        <span
                          :style="picking(option.value)"
                          class="credere-form-field-select-color"
                        >
                          <font-awesome-icon
                            v-if="option.value === CUSTOM_VALUE"
                            class="credere-form-field-select-color-icon"
                            icon="pen"
                          />
                        </span>

                        <label
                          v-if="option.value === CUSTOM_VALUE"
                          class="credere-form-field-select-color-label"
                          @click.stop
                        >
                          <input
                            v-if="option.value === CUSTOM_VALUE"
                            class="credere-form-field-select-color-picker"
                            type="color"
                            v-model="_picking"
                            @change="picked"
                          />
                        </label>
                      </template>

                      {{ option.label }}
                    </span>
                  </label>
                </li>
              </template>

              <li v-else class="credere-form-field-select-text state--empty">
                Nenhum resultado
              </li>
            </ul>
          </div>
        </div>
      </template>
    </template>

    <template v-else-if="uploadable">
      <span class="credere-form-field-file">
        <span class="credere-form-field-file-preview">
          <span
            class="credere-form-field-file-preview-image"
            :style="fileStyle"
          />
        </span>

        <span
          class="credere-form-field-input"
          :tabindex="qrcoding ? 0 : -1"

          @click.capture="qrcoding ? fileToggle() : null"
          @keydown.self.enter="qrcoding ? fileToggle() : null"
          @keydown.self.down="qrcoding ? fileToggle() : null"
          @keydown.self.space="qrcoding ? fileToggle() : null"
        >
          <span class="credere-form-field-file-label">
            {{ value || fileName }}
          </span>

          <credere-button
            :disabled="_disabled || (fileVisible)"
            :loading="loading"

            size="small"
            tabindex="-1"
            @click.stop.prevent
          >
            <template v-if="destroyed">
              Será removido
            </template>

            <template v-else>
              {{ (value || fileName) ? 'Alterar arquivo' : 'Escolher arquivo' }}
            </template>
          </credere-button>

          <input
            v-if="!qrcoding"

            class="credere-form-field-file-input"

            type="file"
            accept="image/*"

            :readonly="_disabled"
            :disabled="_disabled"

            @input="input"
            @change="blur"

            @keydown.enter.prevent
          >
        </span>

        <credere-button
          v-if="filePreview"
          color="danger"
          iconed
          :icon="destroyed ? 'rotate-left' : 'trash'"
          @click.stop.prevent="destroy"
        />
      </span>

      <credere-modal
        v-if="fileVisible"
        class="credere-form-field-modal"
        title="Enviar arquivo"
        inline
        @close="fileToggle"
      >
        <span class="credere-form-field">
          <p class="credere-form-field-label">
            <span class="credere-form-field-legend">
              <b class="credere-form-field-legend-bold">Opção 1:</b>
              Escolha um arquivo do seu computador
            </span>
          </p>

          <span class="credere-form-field-input">
            <span class="credere-form-field-file-label" />

            <credere-button
              size="small"
              tabindex="-1"
              @click="fileToggle"
            >
              Escolher arquivo
            </credere-button>

            <input
              class="credere-form-field-file-input"

              type="file"
              accept="image/*"

              :readonly="_disabled"
              :disabled="_disabled"

              @input="input"
              @change="blur"

              @keydown.enter.prevent
            >
          </span>
        </span>

        <p class="credere-form-field-modal-separator">
          ou
        </p>

        <span class="credere-form-field">
          <p class="credere-form-field-label">
            <span class="credere-form-field-legend">
              <b class="credere-form-field-legend-bold">Opção 2:</b>
              Escaneie o QRCode abaixo e use o seu celular para fotografar o(a)
              <b class="credere-form-field-legend-bold">{{ leaf.label }}</b> agora
            </span>
          </p>
        </span>

        <span class="credere-form-field-input credere-form-field-modal-qrcode">
          <credere-loading class="credere-form-field-modal-qrcode-loading" />

          <qrcode-vue
            v-if="fileLink"
            class="credere-form-field-modal-qrcode-scan"
            :value="fileLink"
            render-as="svg"
            background="#F2F2F2"
            :size="240"
          />
        </span>
      </credere-modal>
    </template>

    <textarea
      v-else-if="properties.type === 'markdown'"
      class="credere-form-field-input"

      :name="name"
      :placeholder="leaf.description"
      :maxlength="properties.maxlength"
      :readonly="_disabled"

      :value="value"
      @input="input"
      @blur="blur"
    />

    <span
      v-else-if="properties.type === 'copy'"

      class="credere-form-field-copy"
    >
      <input
        class="credere-form-field-input"
        readonly
        :value="value"
        @click.prevent="copy"
      >

      <credere-button
        :icon="copied ? null : 'copy'"
        :disabled="copied"
        @click.prevent="copy"
      >
        {{ copied ? 'Copiado!' : 'Copiar' }}
      </credere-button>
    </span>

    <input
      v-else
      class="credere-form-field-input"

      :id="name"
      :name="name"
      :type="properties.type"
      :placeholder="leaf.description"
      :maxlength="properties.maxlength"
      :readonly="_disabled"

      :value="value"
      @input="input"
      @blur="blur"

      @keydown.enter.prevent
    >

    <span
      v-show="error && !destroyed"
      class="credere-form-field-invalidation"
    >
      {{ error }}
    </span>
  </fieldset>
</template>

<style lang="scss">
  @import '@css/mixins/button.scss';
  @import '@css/mixins/reset.scss';

  @mixin disabled-input {
    --background-color: var(--token-color-constant-light);
    --box-shadow: 0 0 0 var(--token-border-width-soft) var(--token-color-neutral-primary);
    --color: var(--token-color-neutral-primary);

    cursor: not-allowed;
  }

  .credere-form-field {
    --font-size: var(--token-font-size-m);
    --gap: var(--token-spacing-micro);
    --line-height: var(--token-line-height-m);

    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--gap);
    justify-content: flex-start;
    position: relative;

    @container (max-width: 768px) {
      --font-size: var(--token-font-size-s);
    }
  }

  .credere-form-field-label {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  .credere-form-field-input {
    --background-color: var(--token-color-neutral-lighter);
    --border-radius: var(--token-border-radius-small);
    --border-width: 0;
    --box-shadow: 0 0 0 var(--token-border-width-soft) var(--token-color-neutral-primary);
    --color: var(--token-color-constant-dark);
    --line-height: var(--token-line-height-s);
    --padding: var(--token-spacing-xxxs) var(--token-spacing-xs);

    appearance: none;
    display: block;
    font: unset;
    opacity: 1;
    outline: none;
    position: relative;
    transition: background-color .1s, box-shadow .1s, color .1s, transform .1s;
    width: 100%;

    &::placeholder {
      --color: var(--token-color-neutral-primary);

      color: var(--color);
    }

    .variant--search & {
      background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="%23999" d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path></svg>');
      background-position: var(--token-spacing-xs) center;
      background-repeat: no-repeat;
      background-size: 1em 1em;
      --padding: var(--token-spacing-xxxs)
                 calc(var(--token-spacing-xs) + 1em + var(--token-spacing-xxxs));

      &::-webkit-search-cancel-button {
        appearance: none;
        background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="%23999" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"></path></svg>');
        background-repeat: no-repeat;
        background-size: contain;
        height: 1em;
        // pointer-events: none;
        cursor: pointer;
        position: absolute;
        right: var(--token-spacing-xxxs);
        top: 50%;
        transform: translateY(-50%);
        width: 1em;
      }
    }

    .variant--field-1b & {
      --border-radius: var(--token-border-radius-none);
    }

    .variant--field-1c & {
      --border-radius: var(--token-border-radius-pill);
    }

    .credere-form-field-checkbox & {
      --background-color: var(--token-color-neutral-light);
      --color: var(--token-color-neutral-darker);
      --box-shadow: none;
      --font-weight: var(--token-font-weight-bold);
      --padding: var(--token-spacing-micro);
    }

    &:focus,
    &:focus-within,
    .credere-form-field-subinput:focus ~ & {
      --box-shadow: 0 0 0 var(--token-border-width-thin) var(--token-color-highlight-primary);

      z-index: 1;
    }

    .state--disabled .credere-form-field-booleans .credere-form-field-subinput ~ & {
      --background-color: var(--token-color-neutral-lighter);
    }

    .credere-form-field-subinput:checked ~ & {
      --background-color: var(--token-color-neutral-darker);
      --color: var(--token-color-constant-light);
    }

    .credere-form-field-checkboxes .credere-form-field-subinput:disabled ~ & {
      @include disabled-input;
    }

    .state--disabled .credere-form-field-subinput:checked ~ & {
      --background-color: var(--token-color-constant-light);
    }

    .credere-form-field-boolean & {
      --border-radius: 0;
    }

    .credere-form-field-boolean:first-child & {
      --border-radius: var(--token-border-radius-small);

      .variant--field-1b & {
        --border-radius: 0;
      }

      .variant--field-1c & {
        --border-radius: var(--token-border-radius-pill);
      }

      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }

    .credere-form-field-boolean:last-child & {
      --border-radius: var(--token-border-radius-small);

      .variant--field-1b & {
        --border-radius: 0;
      }

      .variant--field-1c & {
        --border-radius: var(--token-border-radius-pill);
      }

      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }
  }

  .credere-form-field-invalidation {
    --color: var(--token-color-alert-danger);
  }

  @mixin variants-and-states {
    &.variant--select {
      .credere-form-field-input {
        --gap: var(--token-spacing-xs);

        align-items: center;
        cursor: pointer;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
      }
    }

    &.variant--file {
      .credere-form-field-input {
        --l-height: calc(var(--font-size) * var(--line-height) + var(--token-spacing-micro) * 2);

        --padding:
          var(--token-spacing-mini)
          var(--token-spacing-mini)
          var(--token-spacing-mini)
          var(--token-spacing-xs);

        align-items: center;
        cursor: pointer;
        display: flex;
        flex: 1;
        flex-direction: row;
        justify-content: flex-start;
        line-height: var(--l-height);
        width: 0;
      }
    }

    &.variant--markdown {
      .credere-form-field-input {
        line-height: var(--token-line-height-l);
        min-height: calc(var(--font-size) * var(--line-height) * 7);
        resize: vertical;
      }
    }

    &.variant--destroy {
      .credere-form-field-label {
        @container (max-width: 768px) {
          display: none;
        }
      }
    }

    &.state--disabled {
      .credere-form-field-legend {
        --color: var(--token-color-neutral-primary);
      }

      .credere-form-field-input {
        @include disabled-input;

        &::file-selector-button,
        &::file-selector-button:hover {
          cursor: inherit;

          --background-color: var(--token-color-neutral-light);
          --color: var(--token-color-neutral-primary);
        }
      }

      &.variant--copy {
        .credere-form-field-legend {
          --color: var(--token-color-constant-dark);
        }
      }
    }

    &.state--loading {
      cursor: progress;

      .credere-form-field-input {
        cursor: inherit;
      }
    }

    &.state--error {
      .credere-form-field-legend {
        --color: var(--token-color-alert-danger);
      }

      .credere-form-field-input {
        --box-shadow: 0 0 0 var(--token-border-width-thin) var(--token-color-alert-danger);
      }
    }

    &.state--empty {
      &.variant--select {
        .credere-form-field-input {
          --color: var(--token-color-neutral-primary);
        }
      }
    }
  }

  .credere-form-field-legend-bold {
    --font-weight: var(--token-font-weight-bold);
  }

  .credere-form-field {
    @include variants-and-states;
  }

  .credere-form-field-required {
    --color: var(--token-color-alert-danger);
    --font-weight: var(--token-font-weight-bold);

    display: none;

    .variant--required & {
      display: inline;
    }
  }

  .credere-form-field-sublabel {
    cursor: pointer;
    display: block;
    outline: none;
    position: relative;
  }

  .credere-form-field-subinput {
    cursor: pointer;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  .credere-form-field-destroy {
    --margin: calc(var(--token-border-width-thin) * -1);

    @container (max-width: 768px) {
      &.credere-button.variant--iconed {
        aspect-ratio: auto;
        width: 100%;
      }
    }
  }

  .credere-form-field-booleans {
    align-items: stretch;
    display: flex;
    flex-direction: row;
    list-style: none;
    justify-content: center;
    width: 100%;

    .credere-form-field-destroy + & {
      display: none;
    }
  }

  .credere-form-field-boolean {
    flex: 1;
    text-align: center;
  }

  .credere-form-field-checkboxes {
    --gap: var(--token-spacing-s);

    align-items: stretch;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    list-style: none;
    justify-content: flex-start;
    width: 100%;
  }

  .credere-form-field-checkbox {
    white-space: nowrap;
  }

  .credere-form-field-checkbox-icon {
    --margin: 0 var(--token-spacing-nano) 0 0;
  }

  .credere-form-field-select {
    --background-color: var(--token-color-constant-light);
    --border-radius: var(--token-border-radius-small);
    --border-color: var(--token-color-neutral-lighter);
    --border-style: solid;
    --border-width: var(--token-border-width-soft);
    --box-shadow: var(--token-box-shadow-around-3);
    --color: var(--token-color-constant-dark);

    bottom: auto;
    position: absolute;
    top: calc(100% + var(--token-spacing-micro));
    left: 0;
    width: 100%;
    z-index: 1;
  }

  .credere-form-field-select-overlay {
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1;
  }

  .credere-form-field-select-options {
    --line-height: var(--token-line-height-s);
    --padding: var(--token-spacing-xxxs);

    list-style: none;
    max-height: calc(
      var(--limited, 17rem) - var(--token-spacing-micro) * 2 - var(--token-box-shadow-around-3-y)
    );
    overflow-y: auto;
  }

  .credere-form-field-select-option {
    --margin: var(--token-spacing-micro) 0 0;

    &:first-child {
      --margin: 0;
    }
  }

  .credere-form-field-select-text {
    --border-radius: var(--token-border-radius-small);
    --gap: var(--token-spacing-micro);
    --padding: var(--token-spacing-nano);

    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    &.state--empty {
      --color: var(--token-color-neutral-dark);

      text-align: center;
    }

    .credere-form-field-sublabel:hover &,
    .credere-form-field-subinput:hover ~ & {
      --background-color: var(--token-color-neutral-lighter);
    }

    .credere-form-field-sublabel:focus &,
    .credere-form-field-subinput:focus ~ & {
      --box-shadow: 0 0 0 var(--token-border-width-thin) var(--token-color-highlight-primary);
    }

    .credere-form-field-subinput:disabled ~ & {
      --color: var(--token-color-neutral-primary);
    }
  }

  .credere-form-field-select-color {
    --box-shadow: inset 0 0 0 var(--token-border-width-thin) rgba(51, 51, 51, 0.3);
    --border-radius: var(--token-border-radius-pill);
    --color: var(--picking, rgba(51, 51, 51, 0.3));
    --font-weight: var(--token-font-weight-bold);

    align-items: center;
    display: flex;
    flex-direction: row;
    height: var(--token-spacing-xxl);
    justify-content: center;
    width: var(--token-spacing-xxl);
  }

  .credere-form-field-select-color-label {
    cursor: pointer;
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  .credere-form-field-select-color-picker {
    appearance: none;
    --background-color: transparent;
    --border: none;
    --color: transparent;
    --margin: 0;
    --padding: 0;

    height: 0;
    left: 0;
    opacity: 0;
    outline: none;
    position: absolute;
    top: 100%;
    width: 0;
  }

  .credere-form-field-select-search {
    --background-color: transparent;
    --color: var(--token-color-constant-dark);
    --line-height: var(--token-line-height-s);

    appearance: none;
    display: block;
    flex: 1;
    font: unset;
    opacity: 1;
    outline: none;
    transition: background-color .1s, box-shadow .1s, color .1s, transform .1s;
    width: 100%;

    &::placeholder {
      --color: var(--token-color-neutral-primary);

      color: var(--color);
    }
  }

  .credere-form-field-file {
    --gap: var(--token-spacing-micro);

    align-items: stretch;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    position: relative;
    width: 100%;
  }

  .credere-form-field-file-preview {
    --background-color: var(--token-color-neutral-lighter);
    --border-radius: var(--token-border-radius-small);
    --box-shadow: 0 0 0 var(--token-border-width-soft) var(--token-color-neutral-primary);

    display: block;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
    width: var(--token-spacing-blaster);

    .state--disabled & {
      --background-color: var(--token-color-constant-light);
    }
  }

  .credere-form-field-file-preview-image {
    --margin: auto;

    background-position: center;
    background-size: cover;
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    transition: opacity .1s;
    top: 0;
  }

  .credere-form-field-file-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .credere-form-field-file-input {
    --color: transparent;
    --margin: auto;

    bottom: 0;
    cursor: pointer;
    --font-size: 1px;
    --font-size: 0;
    left: 0;
    outline: none;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;

    .state--disabled & {
      cursor: not-allowed;
    }

    .state--loading & {
      cursor: progress;
    }

    &:focus {
      --border-radius: var(--token-border-radius-small);
      --box-shadow: 0 0 0 var(--token-border-width-thin) var(--token-color-highlight-primary);
    }

    &::file-selector-button,
    &::file-selector-button:hover {
      appearance: none;
      background-color: transparent;
      background-image: none;
      border: none;
      outline: none;
      color: transparent;
      font-size: 1px;
      font-size: 0;
      opacity: 0;
      display: none;
    }
  }

  .credere-form-field-copy {
    --gap: var(--token-spacing-xxxs);

    align-items: stretch;
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
  }

  .credere-form-field-copy-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 0;
  }

  .credere-form-field-modal {
    --margin: var(--token-spacing-xxxs) 0 0;

    .variant--file & {
      .credere-form-field {
        align-items: stretch;
      }

      .credere-form-field-input {
        width: auto;
      }

      .credere-form-field-modal-qrcode {
        --background-color: var(--token-color-neutral-lighter);
        --border-radius: var(--token-border-radius-small);
        --margin: calc(var(--token-spacing-micro) * 2) auto 0;
        --padding: var(--token-spacing-s);

        align-items: center;
        cursor: default;
        display: flex;
        flex-direction: row;
        height: 200px;
        justify-content: center;
        position: relative;
      }
    }
  }

  .credere-form-field-modal-separator {
    --margin: var(--token-spacing-micro) auto;

    text-align: center;
  }

  .credere-form-field-icon {
    --color: var(--token-color-neutral-primary);

    height: 1em;
    left: var(--token-spacing-xs);
    line-height: 1;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translateY(-50%);
    width: 1em;

    .credere-form-field-input:focus ~ & {
      z-index: 1;
    }
  }

  .credere-form-field-modal-qrcode-scan {
    height: calc(100% - var(--token-spacing-s) * 2);
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
  }
</style>
